import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import "mdb-ui-kit/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Input, Ripple, initMDB } from "mdb-ui-kit";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMediaQuery, useTheme } from "@mui/material";
import FooterManual from "./footerManual";
import Notes from "./header";

initMDB({ Input, Ripple, initMDB });

// Constants for Color Palette - RED/LIGHT RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";
const LIGHT_RED_BG = "#FEF2F2";
const EXTRA_LIGHT_RED_HOVER = "#FFF5F5";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
    }),
}));

// Helper component for creating the data cards
const InfoCard = ({ title, icon, children }) => (
    <Box 
        sx={{ 
            border: `2px solid ${DARK_RED_ACCENT}`, 
            marginTop: '20px',
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: `0 4px 12px rgba(220, 38, 38, 0.15)`, 
            transition: "transform 0.3s ease-in-out",
            '&:hover': {
                transform: "translateY(-5px)",
                boxShadow: `0 10px 20px rgba(220, 38, 38, 0.25)`,
            }
        }}
    >
        <Box 
            sx={{ 
                backgroundColor: RED, 
                color: "white", 
                padding: "15px 20px",
                borderBottom: `2px solid ${DARK_RED_ACCENT}` 
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", display: 'flex', alignItems: 'center' }}>
                <i className={`fas fa-${icon}`} style={{ marginRight: '10px' }}></i>
                {title}
            </Typography>
        </Box>
        <Box sx={{ padding: "20px", backgroundColor: "#fff" }}>
            {children}
        </Box>
    </Box>
);

// Helper component for styled list/detail items
const DetailItem = ({ label, value, labelColor, hoverColor }) => (
    <Box 
        sx={{ 
            display: "flex", 
            alignItems: "flex-start",
            padding: "10px 8px",
            borderBottom: "1px solid #E5E7EB",
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: hoverColor || EXTRA_LIGHT_RED_HOVER }, 
            transition: 'background-color 0.2s'
        }}
    >
        <Typography sx={{ 
            fontWeight: "600", 
            minWidth: { xs: "110px", sm: "140px" }, 
            color: labelColor || RED, 
            fontSize: "14px",
            flexShrink: 0
        }}>
            {label}:
        </Typography>
        <Typography sx={{ color: BLACK_TEXT, flex: 1, fontSize: "14px", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>
            {value}
        </Typography>
    </Box>
);

const ShippingPolicy = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    return (
        <Box
            className="container-main-AllSeeModels"
            sx={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#fff"
            }}
        >
            {/* Header/Notes Component */}
            <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

            {/* Banner Section */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
                    backgroundColor: "black",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src="/image/AllSeeModelsBackgroungCar.png" 
                    alt="Automotive Background"
                    sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: 0.35,
                    }}
                />

                <Typography
                    sx={{
                        position: "relative",
                        color: "white",
                        fontWeight: "900",
                        textTransform: "uppercase",
                        letterSpacing: isMobile ? "1px" : isTablet ? "2px" : "4px",
                        fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
                        zIndex: 2,
                        textShadow: "0 0 10px rgba(0,0,0,0.8)",
                        textAlign: "center",
                        lineHeight: 1.1,
                        px: "20px",
                    }}
                >
                    Shipping Policy & Global Logistics
                </Typography>
            </Box>

            {/* Main Content Container */}
            <Box
                sx={{
                    width: isMobile ? "95%" : isTablet ? "92%" : "85%",
                    backgroundColor: "#FFFFFF",
                    margin: "0 auto",
                    marginTop: isMobile ? "-50px" : isTablet ? "-60px" : "-80px",
                    borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
                    padding: isMobile ? "20px 16px" : isTablet ? "32px 24px" : "40px 30px",
                    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
                    zIndex: 10,
                    position: "relative",
                }}
            >
                {/* Introduction Section */}
                <Box sx={{ textAlign: "center", marginBottom: isMobile ? "35px" : isTablet ? "45px" : "60px" }}>
                    <Typography
                        sx={{
                            fontWeight: "800",
                            color: DARK_RED_ACCENT,
                            marginBottom: isMobile ? "10px" : "12px",
                            textTransform: "uppercase",
                            fontSize: isMobile ? "18px" : isTablet ? "24px" : "32px",
                            lineHeight: 1.3,
                        }}
                    >
                        Seamless Global Logistics
                    </Typography>
                    <Typography
                        sx={{
                            color: GRAY_TEXT,
                            fontSize: isMobile ? "13px" : isTablet ? "14px" : "16px",
                            maxWidth: "900px",
                            margin: "0 auto",
                            lineHeight: "1.7",
                            px: isMobile ? "12px" : "0",
                        }}
                    >
                        Amar Enterprises Co Ltd is committed to providing <b>seamless and efficient global logistics</b> for all our products: <b>New & Used Cars, Modified Cars, Auto Parts, Wheels, Tires, and Scraps</b>. Our <b>17 years of expertise</b> ensure your goods reach their destination safely and efficiently.
                    </Typography>
                </Box>

                {/* Scope Box */}
                <Box 
                    sx={{
                        backgroundColor: LIGHT_RED_BG,
                        border: `2px solid ${RED}`,
                        borderRadius: isMobile ? "8px" : "12px",
                        padding: isMobile ? "15px" : isTablet ? "18px" : "20px",
                        marginBottom: isMobile ? "30px" : isTablet ? "40px" : "50px",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: "700",
                            color: RED,
                            fontSize: isMobile ? "14px" : "16px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: "8px",
                        }}
                    >
                        <i className="fas fa-globe" style={{ fontSize: isMobile ? "16px" : "18px" }}></i>
                        Scope: Worldwide Shipping for all Automotive Products
                    </Typography>
                </Box>

                {/* Two-Column Grid: Shipping Options & Timeline */}
                <Box 
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: isMobile ? "20px" : isTablet ? "24px" : "30px",
                        marginBottom: isMobile ? "25px" : isTablet ? "35px" : "40px"
                    }}
                >
                    {/* Column 1: Worldwide Shipping Options */}
                    <InfoCard title="Worldwide Shipping Options" icon="globe-americas">
                        <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : "15px", marginBottom: "15px" }}>
                            We offer global shipping through established carriers, utilizing the most appropriate method based on the size, type, and final destination of your order.
                        </Typography>
                        
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Available Methods</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>RORO (Roll-on/Roll-off), Container Shipping (20ft, 40ft), Air Freight (for parts/urgent shipments)</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Expertise</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>We select the most efficient and cost-effective method for your specific cargo.</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Product Range</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>New & Used Cars, Modified Cars, Auto Parts, Wheels, Tires, and Scraps.</Typography>
                            </Box>
                        </Box>
                    </InfoCard>

                    {/* Column 2: Shipping Process & Timeline */}
                    <InfoCard title="Shipping Process & Timeline" icon="clock">
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Step 1: Documentation</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Initiated upon full and cleared payment (includes mandatory export documentation, e.g., deregistration).</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Step 2: Booking</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>We book the next available vessel for your chosen destination port.</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Transit Time</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Estimated transit times vary (1-6 weeks).</Typography>
                            </Box>
                        </Box>
                        <Typography sx={{ color: RED, fontSize: isMobile ? "11px" : "12px", marginTop: "12px", padding: '8px', backgroundColor: LIGHT_RED_BG, borderRadius: "4px" }}>
                            <b>Disclaimer:</b> All transit times are estimates only and are subject to change by the shipping line due to weather, port delays, or route changes. Amar Enterprises is not responsible for any carrier-related delays.
                        </Typography>
                    </InfoCard>
                </Box>
                
                {/* Allocation of Risk and Cost (Incoterms 2020) */}
                <InfoCard title="Allocation of Risk and Cost (IncotermsÂ® 2020)" icon="file-contract">
                    <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : "15px", marginBottom: "15px" }}>
                        All international trade transactions are governed by the IncotermsÂ® 2020 rules. Your Pro-forma Invoice will specify the exact term used.
                    </Typography>

                    {/* Table-like presentation for Incoterms */}
                    <Box 
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1.5fr 1.5fr" },
                            gap: "10px",
                            backgroundColor: LIGHT_RED_BG,
                            padding: isMobile ? "10px" : "15px",
                            borderRadius: "5px",
                            marginBottom: "15px"
                        }}
                    >
                        <Typography sx={{ fontWeight: "bold", color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px" }}>Incoterm</Typography>
                        <Typography sx={{ fontWeight: "bold", color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px" }}>Seller's Responsibility</Typography>
                        <Typography sx={{ fontWeight: "bold", color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px" }}>Risk Transfer Point</Typography>
                    </Box>

                    {[
                        { 
                            term: 'FOB (Free On Board)', 
                            seller: 'Handles Japanese export clearance and loads the goods onto the vessel at the Japanese port.', 
                            risk: 'Risk transfers to the Buyer once the goods are loaded onto the vessel at the Japanese port.' 
                        },
                        { 
                            term: 'CFR/CIF', 
                            seller: 'Same risk transfer as FOB, but Seller pays for freight (and insurance for CIF) to the destination port.', 
                            risk: 'Risk transfers to the Buyer once the goods are loaded onto the vessel at the Japanese port.' 
                        },
                    ].map((item, index) => (
                        <Box 
                            key={index} 
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1.5fr 1.5fr" },
                                gap: "10px",
                                padding: isMobile ? "10px" : "15px",
                                borderBottom: index < 1 ? "1px solid #E5E7EB" : 'none',
                                '&:hover': { backgroundColor: LIGHT_RED_BG } 
                            }}
                        >
                            <Typography sx={{ fontWeight: "600", color: RED, fontSize: isMobile ? "12px" : "14px" }}>{item.term}</Typography>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>{item.seller}</Typography>
                            <Typography sx={{ color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", fontWeight: "600", lineHeight: "1.4" }}>{item.risk}</Typography>
                        </Box>
                    ))}
                </InfoCard>
                
                {/* Compliance and Documentation - Two-Column Grid */}
                <Box 
                    sx={{
                        marginTop: isMobile ? "20px" : isTablet ? "25px" : "30px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: isMobile ? "20px" : isTablet ? "24px" : "30px",
                    }}
                >
                    {/* Preparation, Compliance, and Buyer Responsibility */}
                    <InfoCard title="Compliance & Buyer Responsibility" icon="exclamation-triangle">
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '8px', fontSize: isMobile ? "12px" : "14px" }}>Vehicle Preparation & Quarantine:</Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Preparation</Typography>
                                        <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>All vehicles undergo a thorough cleaning and preparation process before export.</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Standard</Typography>
                                        <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>This ensures compliance with stringent international quarantine standards (e.g., specific requirements for Australia and New Zealand).</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ backgroundColor: LIGHT_RED_BG, padding: "10px", borderRadius: "6px", borderLeft: `3px solid ${RED}` }}>
                                <Typography sx={{ color: RED, fontWeight: 'bold', marginBottom: '8px', fontSize: isMobile ? "12px" : "14px" }}>Import Compliance Disclaimer:</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "11px" : "13px", marginBottom: "8px", fontWeight: "600" }}>
                                    THE BUYER IS SOLELY AND COMPLETELY RESPONSIBLE FOR IMPORT COMPLIANCE.
                                </Typography>
                                <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "11px" : "13px", lineHeight: "1.5" }}>
                                    The Buyer must confirm and comply with all destination country <b>import laws, tariffs, taxes (VAT/GST), vehicle emission standards, and vehicle age restrictions</b> before placing an order. Amar Enterprises bears no liability for failure to meet these, which may result in goods being seized, delayed, fined, or destroyed.
                                </Typography>
                            </Box>
                        </Box>
                    </InfoCard>
                    
                    {/* Shipping Documentation and Requirements */}
                    <InfoCard title="Shipping Documentation & Requirements" icon="file-invoice">
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '8px', fontSize: isMobile ? "12px" : "14px" }}>Required Consignee Details:</Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Required Data</Typography>
                                        <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Exact, full legal name, complete address, and phone number of the Consignee and Notify Party.</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Amendment Fee</Typography>
                                        <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Any request for amendments to these details after booking will incur substantial amendment fees, which must be paid by the customer.</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            
                            <Box>
                                <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '8px', fontSize: isMobile ? "12px" : "14px" }}>Document Release:</Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Documents</Typography>
                                        <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Original shipping documents (Bill of Lading - B/L, Export Certificate, Commercial Invoice, etc.)</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Release Condition</Typography>
                                        <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Documents will be sent to you via courier only after the full payment for the order has been received and cleared.</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </InfoCard>
                </Box>
            </Box>
            
            {/* Space Filler for Footer Alignment */}
            <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />
            
            {/* Footer Component */}
            <FooterManual />
        </Box>
    );
};

export default ShippingPolicy;